<template>
    <n-form-item
        :label="label"
        :path="path"
        :rule="rule"
        :label-props="labelProps"
        :validation-status="validationStatus"
        :feedback="feedback"
        :show-require-mark="required"
        :require-mark-placement="requireMarkPlacement"
        :show-feedback="showFeedbackMessage"
        :show-label="showLabel"
        :label-style="labelStyle"
        :label-align="labelAlign"
        :feedback-style="feedbackStyle"
        :class="formItemClass"
    >
        <template v-if="label || slots['label-extension']" #label>
            <div class="flex h-4 w-full items-center justify-between">
                <span v-if="label" class="overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-normal text-black-600">
                    {{ label }}
                </span>
                <div v-if="slots['label-extension']" class="flex h-full items-center justify-end">
                    <slot name="label-extension" />
                </div>
            </div>
        </template>
        <slot />
        <template v-if="hasCustomError" #feedback>
            <div :class="customErrorClass">
                {{ customErrorMessage }}
            </div>
        </template>
    </n-form-item>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { NFormItem, type FormItemRule } from 'naive-ui';

interface ValidationParams {
    type?: 'is-danger' | 'is-warning' | 'is-success' | undefined;
    message?: string | undefined;
}

interface Props extends ValidationParams {
    label?: string;
    path?: string;
    rule?: FormItemRule | FormItemRule[];
    labelProps?: Record<string, any>;
    required?: boolean;
    requireMarkPlacement?: 'left' | 'right' | 'right-hanging';
    showFeedback?: boolean;
    showLabel?: boolean;
    labelStyle?: string | Record<string, any>;
    labelAlign?: 'left' | 'center' | 'right';
    feedbackStyle?: string | Record<string, any>;

    // Custom validation integration
    // Style overrides
    customErrorClass?: string;
    formItemClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
    required: false,
    requireMarkPlacement: 'right',
    showFeedback: true,
    showLabel: true,
    labelAlign: 'left',
    customErrorClass: 'text-red-500 text-xs',
    formItemClass: 'mb-0',
});

const slots = useSlots();

const validationStatus = computed(() => {
    if (props.type) {
        switch (props.type) {
            case 'is-danger':
                return 'error';
            case 'is-warning':
                return 'warning';
            case 'is-success':
                return 'success';
            default:
                return undefined;
        }
    }
    return undefined;
});

// Custom error handling
const hasCustomError = computed(() => {
    return props?.type === 'is-danger' && props.message;
});

const customErrorMessage = computed(() => {
    return props?.message || '';
});

// Default feedback (for non-custom validation)
const feedback = computed(() => {
    if (hasCustomError.value) {
        return undefined; // Use custom error template
    }
    return props?.message;
});
const showFeedbackMessage = computed(() => {
    return !!props.message;
});
</script>

<style scoped></style>
